// SB-6637: one `visible_items` event per page load — the id of every shown video thumbnail,
// grouped by its list macro's `data-source` (sub-list); rank = 1-based index in that array.
// `user_panel_*` lists are skipped on purpose: they are private (watch history, likes,
// subscriptions) and would land next to the user id in plaintext access logs.
// There is no per-event flag: the event stops on a revert, or together with every event when
// the global EVENT_TRACKING_ENABLED is off (then window.sbTracker is absent).

import { parseVideoItemId } from '@/analytics/video_item_registry';
import { yieldToMain } from '@/utils/timing';

const LIST_SELECTOR = '.js-media-list[data-source]';
const ITEM_SELECTOR = '.js-video-item';
const SOURCE_PATTERN = /^[a-z0-9_]{1,64}$/;
// Private per-user lists are never sent (SB-6637 plan D11).
const EXCLUDED_SOURCE_PREFIX = 'user_panel_';

/**
 * Budget for the serialised `X-SB-Visible-Items` value. With the header name and the fixed
 * headers (~300 B) one event stays near 4.3 kB, under the tracker's MAX_HEADER_BYTES (6000),
 * so the transport's largest-first whole-field drop can never hit this event.
 */
export const VISIBLE_ITEMS_MAX_BYTES = 4000;

let installed = false;

/** True when `from` or an ancestor below `stopAt` (exclusive; default: up to `<html>`) is display:none. */
const isDisplayNoneUpTo = (from: HTMLElement, stopAt?: HTMLElement): boolean => {
    for (let node: HTMLElement | null = from; node !== null && node !== stopAt; node = node.parentElement) {
        if (window.getComputedStyle(node).display === 'none') return true;
    }
    return false;
};

/**
 * Shown video ids per sub-list, in page DOM order. Lists sharing a source are merged.
 * Over the byte budget the result is an exact DOM-order prefix (ranks stay contiguous).
 */
export const collectVisibleItems = (): Record<string, number[]> => {
    const lists = new Map<string, number[]>();
    let bytes = 2; // `{}`

    for (const root of document.querySelectorAll<HTMLElement>(LIST_SELECTOR)) {
        const source = root.dataset.source ?? '';
        if (!SOURCE_PATTERN.test(source)) continue;
        if (source.startsWith(EXCLUDED_SOURCE_PREFIX)) continue;
        if (isDisplayNoneUpTo(root)) continue;

        for (const item of root.querySelectorAll<HTMLElement>(ITEM_SELECTOR)) {
            if (isDisplayNoneUpTo(item, root)) continue;
            const id = parseVideoItemId(item);
            if (id === null || !Number.isSafeInteger(id) || id <= 0) continue;

            const ids = lists.get(source);
            // New key: `,"src":[id]`; existing key: `,id`.
            const cost =
                ids === undefined
                    ? (lists.size > 0 ? 1 : 0) + JSON.stringify(source).length + 3 + String(id).length
                    : 1 + String(id).length;
            // Stop at the first item that does not fit: skipping ahead to a smaller one would leave a rank gap.
            if (bytes + cost > VISIBLE_ITEMS_MAX_BYTES) return Object.fromEntries(lists);

            bytes += cost;
            if (ids === undefined) {
                lists.set(source, [id]);
            } else {
                ids.push(id);
            }
        }
    }

    return Object.fromEntries(lists);
};

const send = (): void => {
    const lists = collectVisibleItems();
    if (Object.keys(lists).length === 0) return;
    window.sbTracker.track('visible_items', { visible_items: lists });
};

/** Sends one `visible_items` event per page load; call right after `Alpine.start()`. Idempotent. */
export const initVisibleItemsTracker = (): void => {
    if (installed) return;
    installed = true;
    // Typed non-optional, but absent when global event tracking is off: then do nothing at all.
    if (!window.sbTracker) return;

    // Yield first: splits the DOM scan out of the Alpine.start() long task and lets x-cloak removal settle.
    yieldToMain()
        .then(send)
        .catch((error: unknown) => console.error('sbTracker: visible_items collection failed', error));
};
